import Typography from '@/common/components/base/typography/Typography';
import BackwardIcon from '@/modules/Education/assets/icons/backward.svg?react';
import ForwardIcon from '@/modules/Education/assets/icons/forward.svg?react';
import FullOutScreenIcon from '@/modules/Education/assets/icons/full-out-screen.svg?react';
import FullScreenIcon from '@/modules/Education/assets/icons/full-screen.svg?react';
import PauseIcon from '@/modules/Education/assets/icons/pause.svg?react';
import PlayIcon from '@/modules/Education/assets/icons/play.svg?react';
import { useRef } from 'react';
import { css } from 'styled-system/css';
import { Box, Center, Flex } from 'styled-system/jsx';
import useVideoPlayer from '../../hooks/useVideoPlayer';
import { formatTime } from '../../utils/formatTime';

const VideoPlayer = ({ src }: { src: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const {
    playerState,
    togglePlay,
    handleOnTimeUpdate,
    handleVideoProgress,
    handleForward,
    handleBackward,
    toggleFullScreen,
    handleVideoEnded,
    handlePlay,
    handlePause,
    controlsVisible,
    showControls,
  } = useVideoPlayer(videoRef);

  const duration = videoRef.current?.duration || 0;
  const currentTime = videoRef.current?.currentTime || 0;

  return (
    <Box
      onClick={showControls}
      onMouseMove={showControls}
      position="relative"
      width="100%"
      height="100%"
      overflow="hidden"
      bg="#715CAC6B"
      className={css({
        '& video': {
          width: '100%',
          height: '100%',
          display: 'block',
          objectFit: 'contain',
          backgroundColor: 'black',
        },
      })}
    >
      <video
        ref={videoRef}
        src={src}
        autoPlay
        playsInline
        webkit-playsinline="true"
        onEnded={handleVideoEnded}
        onTimeUpdate={handleOnTimeUpdate}
        style={{ transform: `rotate(${playerState.rotate}deg)` }}
        onPlay={handlePlay}
        onPause={handlePause}
      />

      <Box
        position="absolute"
        bottom="0"
        left="0"
        right="0"
        px={10}
        pb={10}
        bg="#715CAC6B"
        display="flex"
        flexDirection="column"
        gap="12px"
        dir="ltr"
        style={{
          opacity: controlsVisible ? 1 : 0,
          transition: 'opacity 0.3s ease',
          pointerEvents: controlsVisible ? 'auto' : 'none',
        }}
      >
        <Box width="100%" position="relative">
          <input
            type="range"
            min="0"
            max="100"
            value={playerState.progress}
            onChange={handleVideoProgress}
            style={{
              background: `linear-gradient(to right, #BCA4FF 0%, #BCA4FF ${playerState.progress}%, white ${playerState.progress}%, white 100%)`,
            }}
            className={css({
              width: '100%',
              height: '6px',
              appearance: 'none',
              borderRadius: 'full',
              outline: 'none',
              cursor: 'pointer',
              margin: '0',
              '&::-webkit-slider-thumb': {
                appearance: 'none',
                WebkitAppearance: 'none',
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                bg: 'purple-2',
                cursor: 'pointer',
              },
            })}
          />

          <Flex justifyContent="space-between" alignItems="center" mt="4px">
            <Typography color="white" textStyle="b1">
              {formatTime(currentTime)}
            </Typography>
            <Typography color="white" textStyle="b1">
              {formatTime(duration)}
            </Typography>
          </Flex>

          <Flex
            alignItems="center"
            justifyContent="center"
            position="relative"
            width="100%"
          >
            <Flex alignItems="center" gap="15px">
              <Center
                cursor="pointer"
                onClick={handleBackward}
                className={css({
                  '& svg': { width: '30px', height: '30px', color: 'white' },
                })}
              >
                <BackwardIcon />
              </Center>

              <Center
                onClick={togglePlay}
                cursor="pointer"
                transition="transform 0.2s"
                _active={{ transform: 'scale(0.95)' }}
                className={css({
                  '& svg': { width: '40px', height: '40px', color: 'white' },
                })}
              >
                {playerState.isPlaying ? <PauseIcon /> : <PlayIcon />}
              </Center>

              <Center
                cursor="pointer"
                onClick={handleForward}
                className={css({
                  '& svg': { width: '32px', height: '32px', color: 'white' },
                })}
              >
                <ForwardIcon />
              </Center>
            </Flex>

            <Box
              position="absolute"
              right="0"
              cursor="pointer"
              onClick={toggleFullScreen}
              className={css({
                '& svg': { width: '24px', height: '24px', color: 'white' },
              })}
            >
              {playerState.isFullscreen ? (
                <FullOutScreenIcon />
              ) : (
                <FullScreenIcon />
              )}
            </Box>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default VideoPlayer;
